import { useState } from 'react';
import { FaBullhorn, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

const Step3AdsAPICredentials = ({ onNext, onBack, onSkip }) => {
  const [credentials, setCredentials] = useState({
    profileId: '',
    accessToken: '',
    refreshToken: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // TODO: Implement Amazon Ads API endpoint
      // await api.post('/credentials/amazon-ads', credentials);
      setSuccess('Amazon Ads credentials saved successfully!');
      setTimeout(() => {
        onNext({ adsApiConfigured: true });
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
          <FaBullhorn className="text-3xl text-yellow-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Connect Amazon Ads (Optional)
        </h2>
        <p className="text-gray-600">
          Manage your advertising campaigns and track performance
        </p>
      </div>

      {/* Optional Badge */}
      <div className="flex justify-center mb-6">
        <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
          Optional - You can skip this step
        </span>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded">
          <div className="flex items-center">
            <FaExclamationCircle className="text-red-500 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded">
          <div className="flex items-center">
            <FaCheckCircle className="text-green-500 mr-2" />
            <p className="text-green-800">{success}</p>
          </div>
        </div>
      )}

      {/* Benefits */}
      <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Why connect Amazon Ads?
        </h3>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Track campaign performance and ROI</span>
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Optimize ad spend with AI insights</span>
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Manage campaigns from one dashboard</span>
          </li>
        </ul>
      </div>

      {/* Form */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Profile ID
          </label>
          <input
            type="text"
            name="profileId"
            value={credentials.profileId}
            onChange={handleChange}
            placeholder="Enter your Amazon Ads Profile ID"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Access Token
          </label>
          <input
            type="password"
            name="accessToken"
            value={credentials.accessToken}
            onChange={handleChange}
            placeholder="Enter your access token"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Refresh Token
          </label>
          <input
            type="password"
            name="refreshToken"
            value={credentials.refreshToken}
            onChange={handleChange}
            placeholder="Enter your refresh token"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Action Button */}
      <button
        onClick={handleSave}
        disabled={loading || !credentials.profileId}
        className="w-full px-6 py-3 bg-yellow-600 text-white font-semibold rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-6"
      >
        {loading ? 'Saving...' : 'Save Amazon Ads Credentials'}
      </button>

      {/* Info Box */}
      <div className="bg-blue-50 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-blue-900 mb-2">
          Don't have Amazon Ads credentials yet?
        </h4>
        <p className="text-sm text-blue-800">
          You can skip this step and configure it later from Settings. 
          Amazon Ads is only needed if you run advertising campaigns.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium"
        >
          ← Back
        </button>
        <div className="flex gap-3">
          <button
            onClick={onSkip}
            className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium"
          >
            Skip for now
          </button>
          <button
            onClick={() => onNext({ adsApiConfigured: false })}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200"
          >
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step3AdsAPICredentials;
